import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AdminDashboard.css';

/**
 * AdminDashboard — Centralized management panel for PNP platform.
 * 
 * Features:
 * - Subscriber visibility (signups, subscriptions)
 * - Subscription lifecycle management (extend trial, activate, cancel)
 * - LOB-scoped RBAC with P-LOB/I-LOB Data Mesh architecture
 * - Service log monitoring (superuser/pnp_operator only)
 * - Audit log viewing
 * 
 * Access:
 * - superuser: Full access to all tabs and features
 * - pnp_operator: Full access to all tabs and features
 * - lob_admin: Scoped access based on assigned LOBs (no service logs, no LOB management)
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper to get auth token
const getAuthToken = () => localStorage.getItem('pnp_access_token') || '';

// Helper to get user role (from token or localStorage)
const getUserRole = () => {
  // In production, this would be extracted from the JWT token
  // For now, check if user is @empirical-ais.com
  const email = localStorage.getItem('pnp_user_email') || '';
  if (email.toLowerCase().endsWith('@empirical-ais.com')) {
    return 'pnp_operator';
  }
  return 'viewer';
};

// API fetch wrapper with auth
const apiFetch = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
      ...options.headers,
    },
  });
  
  if (response.status === 401) {
    // Redirect to login
    window.location.href = '/login';
    throw new Error('Authentication required');
  }
  
  if (response.status === 403) {
    throw new Error('Access denied');
  }
  
  return response;
};

// ═══════════════════════════════════════════════════════════════════════════
// Summary Stats Bar Component
// ═══════════════════════════════════════════════════════════════════════════

const SummaryStatsBar = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="stats-bar loading">
        <div className="stat-card skeleton" />
        <div className="stat-card skeleton" />
        <div className="stat-card skeleton" />
        <div className="stat-card skeleton" />
        <div className="stat-card skeleton" />
      </div>
    );
  }

  return (
    <div className="stats-bar">
      <div className="stat-card">
        <span className="stat-value">{stats?.total_signups || 0}</span>
        <span className="stat-label">Total Signups</span>
      </div>
      <div className="stat-card active">
        <span className="stat-value">{stats?.active_count || 0}</span>
        <span className="stat-label">Active</span>
      </div>
      <div className="stat-card trialing">
        <span className="stat-value">{stats?.trialing_count || 0}</span>
        <span className="stat-label">Trialing</span>
      </div>
      <div className="stat-card cancelled">
        <span className="stat-value">{stats?.cancelled_count || 0}</span>
        <span className="stat-label">Cancelled</span>
      </div>
      <div className="stat-card pending">
        <span className="stat-value">{stats?.pending_count || 0}</span>
        <span className="stat-label">Pending</span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Signups Tab Component (Task 10)
// ═══════════════════════════════════════════════════════════════════════════

const SignupsTab = ({ signups, loading, onRefresh, onSelectSignup }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Extract unique product types for filter dropdown
  const productTypes = [...new Set(signups.map(s => s.product_type).filter(Boolean))].sort();

  // Apply all filters with AND logic (Requirement 7.1-7.5)
  const filteredSignups = signups.filter(signup => {
    // Search filter: company name or email (case-insensitive)
    const matchesSearch = !searchTerm || 
      signup.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      signup.contact_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || signup.status === statusFilter;
    
    // Product type filter
    const matchesProductType = productTypeFilter === 'all' || signup.product_type === productTypeFilter;
    
    return matchesSearch && matchesStatus && matchesProductType;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredSignups.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedSignups = filteredSignups.slice(startIndex, startIndex + pageSize);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, productTypeFilter]);

  if (loading) {
    return <div className="tab-content loading">Loading signups...</div>;
  }

  return (
    <div className="tab-content signups-tab">
      <div className="tab-controls">
        <input
          type="text"
          placeholder="Search by company or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="trial">Trial</option>
          <option value="pending">Pending</option>
          <option value="cancelled">Cancelled</option>
          <option value="inactive">Inactive</option>
        </select>
        <select 
          value={productTypeFilter} 
          onChange={(e) => setProductTypeFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Product Types</option>
          {productTypes.map(pt => (
            <option key={pt} value={pt}>{pt}</option>
          ))}
        </select>
        <button onClick={onRefresh} className="refresh-btn">🔄 Refresh</button>
      </div>

      {filteredSignups.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📝</span>
          <p>No signups found matching your criteria.</p>
        </div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Email</th>
                <th>Product Type</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Trial Start</th>
                <th>Trial End</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSignups.map((signup) => (
                <tr key={signup.client_id}>
                  <td>{signup.company_name}</td>
                  <td>{signup.contact_email}</td>
                  <td><span className="product-type-badge">{signup.product_type}</span></td>
                  <td>{signup.plan || '-'}</td>
                  <td>
                    <span className={`status-badge ${signup.status}`}>
                      {signup.status}
                    </span>
                  </td>
                  <td>
                    {signup.payment_validated ? (
                      <span className="payment-badge validated">✓ Validated</span>
                    ) : (
                      <span className="payment-badge pending">Pending</span>
                    )}
                  </td>
                  <td>{signup.trial_access_start_at ? new Date(signup.trial_access_start_at).toLocaleDateString() : '-'}</td>
                  <td>{signup.trial_access_end_at ? new Date(signup.trial_access_end_at).toLocaleDateString() : '-'}</td>
                  <td>{new Date(signup.created_at).toLocaleDateString()}</td>
                  <td>
                    <button 
                      className="action-btn"
                      onClick={() => onSelectSignup && onSelectSignup(signup)}
                      title="Manage"
                    >
                      ⚙️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="pagination-btn"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                ⏮️ First
              </button>
              <button 
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ◀️ Prev
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages} ({filteredSignups.length} results)
              </span>
              <button 
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next ▶️
              </button>
              <button 
                className="pagination-btn"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last ⏭️
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Subscriptions Tab Component (Task 11)
// ═══════════════════════════════════════════════════════════════════════════

const SubscriptionsTab = ({ subscriptions, loading, onRefresh, onSelectSubscription }) => {
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredSubs = subscriptions.filter(sub => {
    return statusFilter === 'all' || sub.stripe_subscription_status === statusFilter;
  });

  // Calculate summary stats (Requirement 3.5)
  const summaryStats = {
    totalActive: subscriptions.filter(s => s.stripe_subscription_status === 'active').length,
    totalTrialing: subscriptions.filter(s => s.stripe_subscription_status === 'trialing').length,
    // MRR calculation: count active subscriptions * plan price (simplified)
    mrr: subscriptions
      .filter(s => s.stripe_subscription_status === 'active')
      .reduce((sum, s) => {
        const planPrice = s.plan === 'growth' ? 49 : s.plan === 'starter' ? 49 : 0;
        return sum + planPrice;
      }, 0)
  };

  if (loading) {
    return <div className="tab-content loading">Loading subscriptions...</div>;
  }

  return (
    <div className="tab-content subscriptions-tab">
      {/* Summary Bar */}
      <div className="subscription-summary">
        <div className="summary-item">
          <span className="summary-value">{summaryStats.totalActive}</span>
          <span className="summary-label">Active</span>
        </div>
        <div className="summary-item">
          <span className="summary-value">{summaryStats.totalTrialing}</span>
          <span className="summary-label">Trialing</span>
        </div>
        <div className="summary-item mrr">
          <span className="summary-value">${summaryStats.mrr.toLocaleString()}</span>
          <span className="summary-label">Est. MRR</span>
        </div>
      </div>

      <div className="tab-controls">
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="past_due">Past Due</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button onClick={onRefresh} className="refresh-btn">🔄 Refresh</button>
      </div>

      {filteredSubs.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">💳</span>
          <p>No subscriptions found.</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Email</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Period End</th>
              <th>Cancel at End</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubs.map((sub) => (
              <tr key={sub.client_id}>
                <td>{sub.company_name}</td>
                <td>{sub.contact_email}</td>
                <td><span className="plan-badge">{sub.plan || '-'}</span></td>
                <td>
                  <span className={`status-badge ${sub.stripe_subscription_status}`}>
                    {sub.stripe_subscription_status}
                  </span>
                </td>
                <td>{sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '-'}</td>
                <td>
                  {sub.cancel_at_period_end ? (
                    <span className="cancel-badge">⚠️ Cancelling</span>
                  ) : (
                    <span className="active-badge">✓ Active</span>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="action-btn extend"
                      onClick={() => onSelectSubscription && onSelectSubscription(sub, 'extend')}
                      title="Extend Trial"
                      disabled={sub.stripe_subscription_status !== 'trialing'}
                    >
                      ⏰
                    </button>
                    <button 
                      className="action-btn activate"
                      onClick={() => onSelectSubscription && onSelectSubscription(sub, 'activate')}
                      title="Activate"
                      disabled={!['pending', 'inactive', 'cancelled'].includes(sub.status)}
                    >
                      ✅
                    </button>
                    <button 
                      className="action-btn cancel"
                      onClick={() => onSelectSubscription && onSelectSubscription(sub, 'cancel')}
                      title="Cancel"
                      disabled={sub.stripe_subscription_status === 'cancelled'}
                    >
                      ❌
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Action Modals (Task 12)
// ═══════════════════════════════════════════════════════════════════════════

// Cancel Confirmation Modal (Task 12.1)
const CancelConfirmModal = ({ subscriber, onConfirm, onClose, loading }) => {
  if (!subscriber) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>⚠️ Cancel Subscription</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>Are you sure you want to cancel the subscription for:</p>
          <div className="subscriber-info">
            <strong>{subscriber.company_name}</strong>
            <span>{subscriber.contact_email}</span>
          </div>
          <p className="warning-text">
            Access will end at the current billing period: {' '}
            {subscriber.current_period_end 
              ? new Date(subscriber.current_period_end).toLocaleDateString() 
              : 'immediately'}
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button 
            className="btn-danger" 
            onClick={() => onConfirm(subscriber.client_id)}
            disabled={loading}
          >
            {loading ? 'Cancelling...' : 'Confirm Cancellation'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Extend Trial Modal (Task 12.2)
const ExtendTrialModal = ({ subscriber, onConfirm, onClose, loading }) => {
  const [days, setDays] = useState(14);
  const [error, setError] = useState('');

  if (!subscriber) return null;

  const handleSubmit = () => {
    if (days < 1 || days > 90) {
      setError('Days must be between 1 and 90');
      return;
    }
    setError('');
    onConfirm(subscriber.client_id, days);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>⏰ Extend Trial</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>Extend trial period for:</p>
          <div className="subscriber-info">
            <strong>{subscriber.company_name}</strong>
            <span>{subscriber.contact_email}</span>
          </div>
          <div className="form-group">
            <label htmlFor="trial-days">Extension Days (1-90):</label>
            <input
              id="trial-days"
              type="number"
              min="1"
              max="90"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value) || 0)}
              className="form-input"
            />
            {error && <span className="form-error">{error}</span>}
          </div>
          <p className="info-text">
            Current trial ends: {subscriber.trial_access_end_at 
              ? new Date(subscriber.trial_access_end_at).toLocaleDateString() 
              : 'N/A'}
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button 
            className="btn-primary" 
            onClick={handleSubmit}
            disabled={loading || days < 1 || days > 90}
          >
            {loading ? 'Extending...' : `Extend by ${days} Days`}
          </button>
        </div>
      </div>
    </div>
  );
};

// Activate Modal (Task 12.3)
const ActivateModal = ({ subscriber, onConfirm, onClose, loading }) => {
  const [plan, setPlan] = useState('starter');
  const [trialDays, setTrialDays] = useState(14);
  const [error, setError] = useState('');

  if (!subscriber) return null;

  const handleSubmit = () => {
    if (trialDays < 0 || trialDays > 90) {
      setError('Trial days must be between 0 and 90');
      return;
    }
    setError('');
    onConfirm(subscriber.client_id, plan, trialDays);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>✅ Activate Subscriber</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>Activate subscription for:</p>
          <div className="subscriber-info">
            <strong>{subscriber.company_name}</strong>
            <span>{subscriber.contact_email}</span>
          </div>
          <div className="form-group">
            <label htmlFor="plan-select">Plan:</label>
            <select
              id="plan-select"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="form-select"
            >
              <option value="starter">Starter ($49/mo)</option>
              <option value="growth">Growth ($49/mo)</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="trial-days-activate">Trial Days (0-90):</label>
            <input
              id="trial-days-activate"
              type="number"
              min="0"
              max="90"
              value={trialDays}
              onChange={(e) => setTrialDays(parseInt(e.target.value) || 0)}
              className="form-input"
            />
            {error && <span className="form-error">{error}</span>}
            <span className="form-hint">Set to 0 for immediate billing</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button 
            className="btn-success" 
            onClick={handleSubmit}
            disabled={loading || trialDays < 0 || trialDays > 90}
          >
            {loading ? 'Activating...' : 'Activate Account'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Audit Log Tab Component (Task 13)
// ═══════════════════════════════════════════════════════════════════════════

const AuditLogTab = ({ auditLog, loading, onRefresh }) => {
  if (loading) {
    return <div className="tab-content loading">Loading audit log...</div>;
  }

  return (
    <div className="tab-content audit-tab">
      <div className="tab-controls">
        <button onClick={onRefresh} className="refresh-btn">🔄 Refresh</button>
      </div>

      {auditLog.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📜</span>
          <p>No audit entries found.</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Admin</th>
              <th>Action</th>
              <th>Client ID</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {auditLog.map((entry, idx) => (
              <tr key={idx}>
                <td>{new Date(entry.timestamp).toLocaleString()}</td>
                <td>{entry.admin_email || '-'}</td>
                <td>
                  <span className={`action-badge ${entry.action}`}>
                    {entry.action}
                  </span>
                </td>
                <td>{entry.client_id}</td>
                <td className="details-cell">
                  {typeof entry.details === 'string' 
                    ? entry.details.substring(0, 100) 
                    : JSON.stringify(entry.details).substring(0, 100)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Service Logs Tab Component (Task 14 - superuser/pnp_operator only)
// ═══════════════════════════════════════════════════════════════════════════

const ServiceLogsTab = ({ userRole }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterTerm, setFilterTerm] = useState('');
  const [liveTail, setLiveTail] = useState(false);
  const [eventSource, setEventSource] = useState(null);
  const logsContainerRef = useRef(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/v1/admin/service-logs?lines=200');
      const data = await response.json();
      setLogs(data.logs || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-scroll to bottom on initial load
  useEffect(() => {
    if (!loading && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [loading]);

  // Live tail SSE connection (Requirement 11.5, 11.6)
  useEffect(() => {
    if (liveTail) {
      const token = getAuthToken();
      const url = `${API_BASE}/v1/admin/service-logs/stream`;
      
      // Create EventSource with auth header workaround
      const es = new EventSource(url);
      
      es.onmessage = (event) => {
        try {
          const logEntry = JSON.parse(event.data);
          setLogs(prev => {
            const newLogs = [...prev, logEntry];
            // Keep only last 1000 entries to prevent memory issues
            return newLogs.slice(-1000);
          });
          
          // Auto-scroll when live tail is active
          if (logsContainerRef.current) {
            logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
          }
        } catch (e) {
          // Handle plain text log entries
          setLogs(prev => [...prev.slice(-999), { 
            raw: event.data, 
            timestamp: new Date().toISOString(),
            level: 'INFO',
            message: event.data 
          }]);
        }
      };

      es.onerror = (err) => {
        console.error('SSE error:', err);
        setError('Live tail connection lost. Reconnecting...');
        es.close();
        // Attempt reconnect after 3 seconds
        setTimeout(() => {
          if (liveTail) {
            setLiveTail(false);
            setTimeout(() => setLiveTail(true), 100);
          }
        }, 3000);
      };

      setEventSource(es);

      return () => {
        es.close();
        setEventSource(null);
      };
    } else {
      // Stop SSE when live tail is disabled (Requirement 11.6)
      if (eventSource) {
        eventSource.close();
        setEventSource(null);
      }
    }
  }, [liveTail]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [eventSource]);

  // Filter logs (Requirement 11.10, 11.11)
  const filteredLogs = logs.filter(log => {
    if (!filterTerm) return true;
    const searchTerm = filterTerm.toLowerCase();
    return (
      log.message?.toLowerCase().includes(searchTerm) ||
      log.raw?.toLowerCase().includes(searchTerm) ||
      log.level?.toLowerCase().includes(searchTerm)
    );
  });

  if (!['superuser', 'pnp_operator'].includes(userRole)) {
    return (
      <div className="tab-content access-denied">
        <span className="denied-icon">🔒</span>
        <p>Service logs require superuser or pnp_operator role.</p>
      </div>
    );
  }

  if (loading && logs.length === 0) {
    return <div className="tab-content loading">Loading service logs...</div>;
  }

  if (error && logs.length === 0) {
    return (
      <div className="tab-content error">
        <span className="error-icon">⚠️</span>
        <p>Failed to load logs: {error}</p>
        <button onClick={fetchLogs} className="retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="tab-content service-logs-tab">
      <div className="tab-controls">
        <input
          type="text"
          placeholder="Filter logs (case-insensitive)..."
          value={filterTerm}
          onChange={(e) => setFilterTerm(e.target.value)}
          className="search-input"
        />
        <label className="live-tail-toggle">
          <input
            type="checkbox"
            checked={liveTail}
            onChange={(e) => setLiveTail(e.target.checked)}
          />
          <span className={`live-indicator ${liveTail ? 'active' : ''}`}>●</span>
          Live Tail
        </label>
        <button onClick={fetchLogs} className="refresh-btn" disabled={liveTail}>
          🔄 Refresh
        </button>
        <span className="log-count">{filteredLogs.length} entries</span>
      </div>

      {error && (
        <div className="log-error-banner">
          ⚠️ {error}
        </div>
      )}

      <div className="logs-container" ref={logsContainerRef}>
        {filteredLogs.length === 0 ? (
          <div className="empty-logs">
            <p>No log entries match your filter.</p>
          </div>
        ) : (
          filteredLogs.map((log, idx) => (
            <div key={idx} className={`log-entry ${log.level?.toLowerCase()}`}>
              <span className="log-timestamp">{log.timestamp || '-'}</span>
              <span className={`log-level ${log.level?.toLowerCase()}`}>
                {log.level || 'LOG'}
              </span>
              <span className="log-message">{log.message || log.raw}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// LOB Selector Component (Task 15.1)
// ═══════════════════════════════════════════════════════════════════════════

const LOBSelector = ({ userRole, lobTree, selectedLOB, onSelectLOB }) => {
  const allLOBs = [
    { lob_id: 'all', lob_name: 'All LOBs', lob_type: 'all' },
    ...(lobTree.pure_lobs || []).map(lob => ({ ...lob, lob_type: 'pure' })),
    ...(lobTree.impure_lobs || []).map(lob => ({ ...lob, lob_type: 'impure' }))
  ];

  // For lob_admin, only show assigned LOBs
  const availableLOBs = userRole === 'lob_admin' 
    ? allLOBs.filter(lob => lob.lob_id === 'all' || lob.assigned)
    : allLOBs;

  return (
    <div className="lob-selector">
      <label htmlFor="lob-select">LOB Context:</label>
      <select
        id="lob-select"
        value={selectedLOB?.lob_id || 'all'}
        onChange={(e) => {
          const selected = availableLOBs.find(l => l.lob_id === e.target.value);
          onSelectLOB(selected);
        }}
        className="lob-select"
      >
        {availableLOBs.map(lob => (
          <option key={lob.lob_id} value={lob.lob_id}>
            {lob.lob_type === 'pure' ? '📁 ' : lob.lob_type === 'impure' ? '🔗 ' : '🌐 '}
            {lob.lob_name}
          </option>
        ))}
      </select>
      {selectedLOB && selectedLOB.lob_id !== 'all' && (
        <span className="lob-context-badge">
          {selectedLOB.lob_type === 'pure' ? 'P-LOB' : 'I-LOB'}
        </span>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// I-LOB Agents Panel (Task 15.3 - Read-only placeholder)
// ═══════════════════════════════════════════════════════════════════════════

const ILOBAgentsPanel = ({ userRole }) => {
  // Placeholder/mock data for A2A agents (future phase)
  const mockAgents = [
    { agent_id: 'agent-001', name: 'DataSync Agent', status: 'active', task: 'Syncing customer records', subscription: 'enterprise' },
    { agent_id: 'agent-002', name: 'Analytics Bot', status: 'idle', task: 'Waiting for tasks', subscription: 'growth' },
    { agent_id: 'agent-003', name: 'Report Generator', status: 'offline', task: 'N/A', subscription: 'starter' },
  ];

  return (
    <div className="ilob-agents-panel">
      <h5>🤖 A2A Agents (Preview)</h5>
      <p className="panel-note">Agent integration coming in future release</p>
      <div className="agents-list">
        {mockAgents.map(agent => (
          <div key={agent.agent_id} className={`agent-card ${agent.status}`}>
            <div className="agent-header">
              <span className="agent-name">{agent.name}</span>
              <span className={`agent-status ${agent.status}`}>{agent.status}</span>
            </div>
            <div className="agent-details">
              <span className="agent-task">{agent.task}</span>
              <span className="agent-subscription">{agent.subscription}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// LOB Management Tab Component (Task 15.2 - superuser/pnp_operator only)
// ═══════════════════════════════════════════════════════════════════════════

const LOBManagementTab = ({ userRole }) => {
  const [lobTree, setLobTree] = useState({ pure_lobs: [], impure_lobs: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddLOBForm, setShowAddLOBForm] = useState(false);
  const [showAssignAdminForm, setShowAssignAdminForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  
  // Add LOB form state
  const [newLOB, setNewLOB] = useState({
    lob_name: '',
    lob_type: 'pure',
    parent_lob_id: '',
    product_types: []
  });
  
  // Assign Admin form state
  const [assignAdmin, setAssignAdmin] = useState({
    admin_user_id: '',
    lob_id: ''
  });

  const fetchLOBs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/v1/admin/lobs');
      const data = await response.json();
      setLobTree(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLOBs();
  }, [fetchLOBs]);

  // Handle Add LOB submission
  const handleAddLOB = async (e) => {
    e.preventDefault();
    try {
      setFormLoading(true);
      const response = await apiFetch('/v1/admin/lobs', {
        method: 'POST',
        body: JSON.stringify({
          lob_name: newLOB.lob_name,
          lob_type: newLOB.lob_type,
          parent_lob_id: newLOB.parent_lob_id || null,
          product_types: newLOB.product_types
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create LOB');
      }
      
      setShowAddLOBForm(false);
      setNewLOB({ lob_name: '', lob_type: 'pure', parent_lob_id: '', product_types: [] });
      fetchLOBs();
    } catch (err) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Handle Assign Admin submission
  const handleAssignAdmin = async (e) => {
    e.preventDefault();
    try {
      setFormLoading(true);
      const response = await apiFetch('/v1/admin/lob-assignments', {
        method: 'POST',
        body: JSON.stringify({
          admin_user_id: assignAdmin.admin_user_id,
          lob_id: assignAdmin.lob_id
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to assign admin');
      }
      
      setShowAssignAdminForm(false);
      setAssignAdmin({ admin_user_id: '', lob_id: '' });
      fetchLOBs();
    } catch (err) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  if (!['superuser', 'pnp_operator'].includes(userRole)) {
    return (
      <div className="tab-content access-denied">
        <span className="denied-icon">🔒</span>
        <p>LOB management requires superuser or pnp_operator role.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="tab-content loading">Loading LOB hierarchy...</div>;
  }

  if (error && !lobTree.pure_lobs?.length && !lobTree.impure_lobs?.length) {
    return (
      <div className="tab-content error">
        <span className="error-icon">⚠️</span>
        <p>Failed to load LOBs: {error}</p>
        <button onClick={fetchLOBs} className="retry-btn">Retry</button>
      </div>
    );
  }

  const renderLOBTree = (lobs, depth = 0) => {
    return lobs.map(lob => (
      <div key={lob.lob_id} className="lob-node" style={{ marginLeft: depth * 20 }}>
        <div className="lob-header">
          <span className="lob-expand">{lob.children?.length > 0 ? '▼' : '•'}</span>
          <span className="lob-icon">{lob.lob_type === 'pure' ? '📁' : '🔗'}</span>
          <span className="lob-name">{lob.lob_name}</span>
          <span className={`lob-status ${lob.status}`}>{lob.status}</span>
          <span className="lob-admin-count" title="Assigned admins">
            👤 {lob.admin_count || 0}
          </span>
        </div>
        <div className="lob-details">
          <span className="lob-type">{lob.lob_type === 'pure' ? 'P-LOB' : 'I-LOB'}</span>
          {lob.product_types?.length > 0 && (
            <span className="lob-products">
              Products: {lob.product_types.join(', ')}
            </span>
          )}
        </div>
        {lob.children?.length > 0 && renderLOBTree(lob.children, depth + 1)}
      </div>
    ));
  };

  // Flatten P-LOBs for parent selector
  const flattenLOBs = (lobs, result = []) => {
    lobs.forEach(lob => {
      result.push(lob);
      if (lob.children) flattenLOBs(lob.children, result);
    });
    return result;
  };
  const flatPLOBs = flattenLOBs(lobTree.pure_lobs || []);

  return (
    <div className="tab-content lob-management-tab">
      <div className="tab-controls">
        <button 
          className="btn-primary"
          onClick={() => setShowAddLOBForm(!showAddLOBForm)}
        >
          ➕ Add LOB
        </button>
        <button 
          className="btn-secondary"
          onClick={() => setShowAssignAdminForm(!showAssignAdminForm)}
        >
          👤 Assign Admin
        </button>
        <button onClick={fetchLOBs} className="refresh-btn">🔄 Refresh</button>
      </div>

      {error && (
        <div className="log-error-banner">⚠️ {error}</div>
      )}

      {/* Add LOB Form */}
      {showAddLOBForm && (
        <div className="inline-form">
          <h4>Create New LOB</h4>
          <form onSubmit={handleAddLOB}>
            <div className="form-row">
              <div className="form-group">
                <label>LOB Name:</label>
                <input
                  type="text"
                  value={newLOB.lob_name}
                  onChange={(e) => setNewLOB({ ...newLOB, lob_name: e.target.value })}
                  required
                  className="form-input"
                  placeholder="Enter LOB name"
                />
              </div>
              <div className="form-group">
                <label>LOB Type:</label>
                <select
                  value={newLOB.lob_type}
                  onChange={(e) => setNewLOB({ 
                    ...newLOB, 
                    lob_type: e.target.value,
                    parent_lob_id: e.target.value === 'impure' ? '' : newLOB.parent_lob_id
                  })}
                  className="form-select"
                >
                  <option value="pure">Pure (P-LOB)</option>
                  <option value="impure">Impure (I-LOB)</option>
                </select>
              </div>
              {newLOB.lob_type === 'pure' && (
                <div className="form-group">
                  <label>Parent P-LOB (optional):</label>
                  <select
                    value={newLOB.parent_lob_id}
                    onChange={(e) => setNewLOB({ ...newLOB, parent_lob_id: e.target.value })}
                    className="form-select"
                  >
                    <option value="">None (Top-level)</option>
                    {flatPLOBs.map(lob => (
                      <option key={lob.lob_id} value={lob.lob_id}>{lob.lob_name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Product Types (comma-separated):</label>
              <input
                type="text"
                value={newLOB.product_types.join(', ')}
                onChange={(e) => setNewLOB({ 
                  ...newLOB, 
                  product_types: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                })}
                className="form-input"
                placeholder="e.g., restaurant, retail, healthcare"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-success" disabled={formLoading}>
                {formLoading ? 'Creating...' : 'Create LOB'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setShowAddLOBForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Assign Admin Form */}
      {showAssignAdminForm && (
        <div className="inline-form">
          <h4>Assign Admin to P-LOB</h4>
          <form onSubmit={handleAssignAdmin}>
            <div className="form-row">
              <div className="form-group">
                <label>Admin User ID:</label>
                <input
                  type="text"
                  value={assignAdmin.admin_user_id}
                  onChange={(e) => setAssignAdmin({ ...assignAdmin, admin_user_id: e.target.value })}
                  required
                  className="form-input"
                  placeholder="Enter Cognito user ID"
                />
              </div>
              <div className="form-group">
                <label>P-LOB:</label>
                <select
                  value={assignAdmin.lob_id}
                  onChange={(e) => setAssignAdmin({ ...assignAdmin, lob_id: e.target.value })}
                  required
                  className="form-select"
                >
                  <option value="">Select P-LOB</option>
                  {flatPLOBs.map(lob => (
                    <option key={lob.lob_id} value={lob.lob_id}>{lob.lob_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-success" disabled={formLoading}>
                {formLoading ? 'Assigning...' : 'Assign Admin'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setShowAssignAdminForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="lob-section">
        <h3>📊 Data Mesh Architecture — P-LOB / I-LOB Zones</h3>
        
        <div className="lob-zone p-lob-zone">
          <h4>🏢 P-LOB Zone (Pure Lines of Business)</h4>
          <p className="zone-description">
            Hierarchical RBAC-scoped domains. Admin users are assigned to P-LOBs 
            and can only see data within their assigned scope.
          </p>
          {lobTree.pure_lobs?.length > 0 ? (
            <div className="lob-tree">
              {renderLOBTree(lobTree.pure_lobs)}
            </div>
          ) : (
            <div className="empty-state">
              <p>No P-LOBs configured. Create your first P-LOB to define business domains.</p>
            </div>
          )}
        </div>

        <div className="lob-zone i-lob-zone">
          <h4>🤖 I-LOB Zone (Impure Lines of Business)</h4>
          <p className="zone-description">
            Flat A2A agent integration layer. I-LOBs represent external agent 
            connections that operate across P-LOB boundaries.
          </p>
          {lobTree.impure_lobs?.length > 0 ? (
            <div className="lob-list">
              {lobTree.impure_lobs.map(lob => (
                <div key={lob.lob_id} className="lob-node i-lob">
                  <span className="lob-icon">🔗</span>
                  <span className="lob-name">{lob.lob_name}</span>
                  <span className={`lob-status ${lob.status}`}>{lob.status}</span>
                  <span className="lob-agent-count" title="Linked agents">
                    🤖 {lob.agent_count || 0}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No I-LOBs configured. I-LOBs will be created when A2A agents are registered.</p>
            </div>
          )}
          
          {/* I-LOB Agents Panel (placeholder) */}
          <ILOBAgentsPanel userRole={userRole} />
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Main AdminDashboard Component
// ═══════════════════════════════════════════════════════════════════════════

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('signups');
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [modalType, setModalType] = useState(null); // 'cancel', 'extend', 'activate'
  const [modalLoading, setModalLoading] = useState(false);
  
  // Toast notifications
  const [toasts, setToasts] = useState([]);

  const userRole = getUserRole();
  const userEmail = localStorage.getItem('pnp_user_email') || '';

  // Toast helper
  const showToast = (message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  };

  // Define available tabs based on role
  const tabs = [
    { id: 'signups', label: 'Recent Signups', icon: '📝' },
    { id: 'subscriptions', label: 'Subscriptions', icon: '💳' },
    { id: 'audit-log', label: 'Audit Log', icon: '📜' },
    // Service Logs only for superuser/pnp_operator
    ...(['superuser', 'pnp_operator'].includes(userRole) 
      ? [{ id: 'service-logs', label: 'Service Logs', icon: '📋' }] 
      : []),
    // LOB Management only for superuser/pnp_operator
    ...(['superuser', 'pnp_operator'].includes(userRole) 
      ? [{ id: 'lob-management', label: 'LOB Management', icon: '🏢' }] 
      : []),
  ];

  // Fetch dashboard data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch stats
      const statsRes = await apiFetch('/v1/admin/stats');
      const statsData = await statsRes.json();
      setStats(statsData);

      // Fetch signups
      const signupsRes = await apiFetch('/v1/admin/signups?limit=100');
      const signupsData = await signupsRes.json();
      setSignups(signupsData.signups || []);

      // Fetch subscriptions
      const subsRes = await apiFetch('/v1/admin/subscriptions');
      const subsData = await subsRes.json();
      setSubscriptions(subsData.subscriptions || []);

      // Fetch audit log
      const auditRes = await apiFetch('/v1/admin/audit-log');
      const auditData = await auditRes.json();
      setAuditLog(auditData.entries || []);

    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Modal handlers
  const openModal = (subscriber, type) => {
    setSelectedSubscriber(subscriber);
    setModalType(type);
  };

  const closeModal = () => {
    setSelectedSubscriber(null);
    setModalType(null);
  };

  // Action handlers
  const handleCancel = async (clientId) => {
    try {
      setModalLoading(true);
      const response = await apiFetch(`/v1/subscribers/${clientId}/cancel-subscription`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }
      
      showToast('Subscription cancelled successfully', 'success');
      closeModal();
      fetchData(); // Refresh data
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleExtendTrial = async (clientId, days) => {
    try {
      setModalLoading(true);
      const response = await apiFetch('/v1/admin/extend-trial', {
        method: 'POST',
        body: JSON.stringify({ client_id: clientId, days })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to extend trial');
      }
      
      showToast(`Trial extended by ${days} days`, 'success');
      closeModal();
      fetchData(); // Refresh data
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleActivate = async (clientId, plan, trialDays) => {
    try {
      setModalLoading(true);
      const response = await apiFetch('/v1/admin/activate', {
        method: 'POST',
        body: JSON.stringify({ client_id: clientId, plan, trial_days: trialDays })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to activate account');
      }
      
      showToast('Account activated successfully', 'success');
      closeModal();
      fetchData(); // Refresh data
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Check if user has admin access
  if (!['superuser', 'pnp_operator', 'lob_admin'].includes(userRole)) {
    return (
      <div className="admin-dashboard access-denied">
        <div className="denied-content">
          <span className="denied-icon">🔒</span>
          <h2>Access Denied</h2>
          <p>You do not have permission to access the Admin Dashboard.</p>
          <p>Required roles: superuser, pnp_operator, or lob_admin</p>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'signups':
        return (
          <SignupsTab 
            signups={signups} 
            loading={loading} 
            onRefresh={fetchData}
            onSelectSignup={(signup) => openModal(signup, 'activate')}
          />
        );
      case 'subscriptions':
        return (
          <SubscriptionsTab 
            subscriptions={subscriptions} 
            loading={loading} 
            onRefresh={fetchData}
            onSelectSubscription={openModal}
          />
        );
      case 'audit-log':
        return <AuditLogTab auditLog={auditLog} loading={loading} onRefresh={fetchData} />;
      case 'service-logs':
        return <ServiceLogsTab userRole={userRole} />;
      case 'lob-management':
        return <LOBManagementTab userRole={userRole} />;
      default:
        return (
          <SignupsTab 
            signups={signups} 
            loading={loading} 
            onRefresh={fetchData}
            onSelectSignup={(signup) => openModal(signup, 'activate')}
          />
        );
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="header-title">
          <h2>🛡️ Admin Dashboard</h2>
          <span className="user-context">
            {userEmail} • {userRole}
          </span>
        </div>
        {error && (
          <div className="error-banner">
            ⚠️ {error}
            <button onClick={fetchData} className="retry-btn">Retry</button>
          </div>
        )}
      </div>

      <SummaryStatsBar stats={stats} loading={loading} />

      <div className="tab-navigation">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {renderTabContent()}

      {/* Action Modals */}
      {modalType === 'cancel' && (
        <CancelConfirmModal
          subscriber={selectedSubscriber}
          onConfirm={handleCancel}
          onClose={closeModal}
          loading={modalLoading}
        />
      )}
      {modalType === 'extend' && (
        <ExtendTrialModal
          subscriber={selectedSubscriber}
          onConfirm={handleExtendTrial}
          onClose={closeModal}
          loading={modalLoading}
        />
      )}
      {modalType === 'activate' && (
        <ActivateModal
          subscriber={selectedSubscriber}
          onConfirm={handleActivate}
          onClose={closeModal}
          loading={modalLoading}
        />
      )}

      {/* Toast Notifications */}
      {toasts.length > 0 && (
        <div className="toast-container">
          {toasts.map(toast => (
            <div key={toast.id} className={`toast ${toast.type}`}>
              <span>{toast.message}</span>
              <button 
                className="toast-close"
                onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
